import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useSnackbar } from 'notistack';
import { DismissError } from '../redux/actions/error/error';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Link } from '@mui/material';
import InsufficientCreditsModal from '../components/billing/InsufficientCreditsModal';

const Error = ({ error, errorDimiss }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    
    // Track if we've already shown the "Please select a workspace" message
    const [hasShownWorkspaceMessage, setHasShownWorkspaceMessage] = useState(false);
    const [limitDialog, setLimitDialog] = useState(false);
    const [limitTitle, setLimitTitle] = useState('Plan limit reached');
    const [limitMessage, setLimitMessage] = useState('');
    // Structured plan lifecycle verdict from the server (status / effect /
    // grace_ends_at / ...) — used to tailor the dialog details.
    const [limitPlanState, setLimitPlanState] = useState(null);

    const handleCloseLimitDialog = () => {
        setLimitDialog(false);
    };

    const handleNavigateToBilling = () => {
        setLimitDialog(false);
        navigate('/settings/billing');
    };

    // Phase 13.F: insufficient credits modal state.
    const [insufficientCredits, setInsufficientCredits] = useState(null);
    const handleCloseInsufficient = () => setInsufficientCredits(null);

    // Reset the workspace message flag when a workspace is selected
    useEffect(() => {
        const workspaceId = localStorage.getItem('WORKSPACE_ID');
        if (workspaceId && hasShownWorkspaceMessage) {
            setHasShownWorkspaceMessage(false);
        }
    }, [hasShownWorkspaceMessage]);

    useEffect(() => {
        if (error) {
            if (error.response?.status === 401) {
                if (error.response?.data?.status === 'unauthorized_for_workspace') {
                    // The selected workspace is stale / inaccessible (e.g. it was
                    // deleted, or it's a leftover id from a previous session).
                    // Silently drop the selection and let App-level reconciliation
                    // pick a valid workspace or route a brand-new user to
                    // onboarding — no scary "select a workspace" prompt.
                    try { localStorage.removeItem('WORKSPACE_ID'); } catch (_) { /* ignore */ }
                    errorDimiss();
                } else {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user');
                    localStorage.removeItem('pendingEmail');
                    errorDimiss();
                    // Redirect to static login page
                    window.location.href = '/login.html';
                }
            } else if (error.response?.status === 403) {
                // Plan-gate 403s carry a server-built ``message`` ("The Team
                // plan expired on Sep 11, 2026. The organization is
                // read-only …") plus a structured ``plan_state``. Prefer
                // that over the generic copy so the user learns *what*
                // expired, *when*, and what the effect is.
                const data = error.response?.data || {};
                const planState = data.plan_state || null;
                const serverMsg = typeof data.message === 'string' ? data.message : '';
                if (data.status === 'inactive_plan') {
                    enqueueSnackbar('Your plan is inactive. Please update your subscription.', { variant: 'error' });
                    setLimitTitle('Plan inactive');
                    setLimitMessage(serverMsg || 'Your plan is currently inactive. To continue using all features, please update your subscription.');
                    setLimitPlanState(planState);
                    setLimitDialog(true);
                }
                else if (data.status === 'subscription_expired') {
                    const isOrg = planState?.effect === 'read_only';
                    enqueueSnackbar(
                        isOrg
                            ? 'Your organization\'s plan has expired — the workspace is read-only until it is renewed.'
                            : 'Your plan has expired. Renew it to continue.',
                        { variant: 'error' },
                    );
                    setLimitTitle('Plan expired');
                    setLimitMessage(serverMsg || 'Your subscription has expired. To continue using all features, please renew your subscription.');
                    setLimitPlanState(planState);
                    setLimitDialog(true);
                }
                else if (data.status === 'low_quota') {
                    enqueueSnackbar('You have reached your quota limit. Please upgrade your plan.', { variant: 'error' });
                    setLimitTitle('Plan limit reached');
                    setLimitMessage('You have reached the limit of your current plan. To continue, please upgrade to a plan with higher limits.');
                    setLimitPlanState(null);
                    setLimitDialog(true);
                }
                else if (typeof data.status === 'string' && data.status.includes('Your plan allows a maximum of')) {
                    // Handle specific plan limit errors
                    enqueueSnackbar(data.status, { variant: 'error' });
                    setLimitTitle('Plan limit reached');
                    setLimitMessage(data.status + ' To continue, please upgrade to a plan with higher limits.');
                    setLimitPlanState(null);
                    setLimitDialog(true);
                }
                else {
                    // Some endpoints (e.g. model promotion) return a coded
                    // envelope {error: {code, message, details}} instead of a
                    // flat ``status`` string. Surface that message so plan /
                    // quota gates read as themselves instead of a misleading
                    // "Access denied: Unauthorized".
                    const coded = error.response?.data?.error;
                    const codedMsg = (coded && typeof coded === 'object') ? coded.message : coded;
                    enqueueSnackbar(
                        codedMsg || ('Access denied: ' + (error.response?.data?.status || 'Unauthorized')),
                        { variant: 'error' },
                    );
                }
                errorDimiss();
            } else if (error.response?.status === 402) {
                // Phase 13.F: insufficient credits. Show the dedicated
                // modal rather than the snackbar so the user gets the
                // "Top up" CTA + clear context.
                const detail = error.response?.data?.detail || error.response?.data || {};
                setInsufficientCredits({
                    needed: detail.needed,
                    operation: detail.operation,
                    message: detail.message || 'Insufficient credits to complete this action.',
                });
                errorDimiss();
            } else if (error.response?.data?.status === 'projectid_required'
                       || error.response?.data?.status === 'workspaceid_required'
                       // The backend's ``workspace_required`` decorator returns this
                       // human string (HTTP 400); treat it the same as the coded
                       // variants so a transient no-workspace state doesn't surface a
                       // scary raw error.
                       || error.response?.data?.status === 'Workspace ID required') {
                errorDimiss();
                // Don't nag on pages that legitimately have no workspace selected
                // (workspace detail/create/list, onboarding).
                const currentPath = window.location.pathname;
                const SAFE = ['/workspace/', '/workspaces', '/new-workspace', '/onboarding'];
                const onSafe = SAFE.some((p) => currentPath.startsWith(p));
                if (!onSafe && !hasShownWorkspaceMessage) {
                    enqueueSnackbar('Please select a workspace to continue', { variant: 'error' });
                    setHasShownWorkspaceMessage(true);
                    navigate('/workspaces');
                }
            } else if (error.response?.data?.status) {
                errorDimiss();
                enqueueSnackbar(error.response?.data?.status, { variant: 'error' });
            } else {
                // Uncaught Error
                errorDimiss();
                // enqueueSnackbar('An unexpected error occurred. Please try again later.', { variant: 'error' });
            }
        }
    }, [error, navigate, enqueueSnackbar, errorDimiss]);

    return (
        <>
            <Dialog
                open={limitDialog}
                onClose={handleCloseLimitDialog}
                aria-labelledby="plan-limit-dialog-title"
                aria-describedby="plan-limit-dialog-description"
            >
                <DialogTitle id="plan-limit-dialog-title">
                    {limitTitle}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="plan-limit-dialog-description">
                        {limitMessage}
                    </DialogContentText>
                    {limitPlanState?.status === 'expired' && limitPlanState?.grace_ends_at && (
                        <DialogContentText sx={{ mt: 1.5, fontSize: 13 }}>
                            {limitPlanState.effect === 'read_only'
                                ? 'Scheduled goals, pipelines and deployments will be paused on '
                                : 'Resources above the Free-plan limits will be paused on '}
                            {new Date(limitPlanState.grace_ends_at).toLocaleDateString(undefined, {
                                month: 'short', day: 'numeric', year: 'numeric',
                            })}
                            {' if the plan is not renewed before then.'}
                        </DialogContentText>
                    )}
                    {limitPlanState?.status === 'suspended' && (
                        <DialogContentText sx={{ mt: 1.5, fontSize: 13 }}>
                            Paused automations resume automatically once the plan is renewed.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLimitDialog} color="primary">
                        Continue
                    </Button>
                    <Button onClick={handleNavigateToBilling} color="primary" variant="contained">
                        {limitPlanState?.status === 'expired' || limitPlanState?.status === 'suspended'
                            ? 'Renew plan'
                            : 'View plans'}
                    </Button>
                </DialogActions>
            </Dialog>
            <InsufficientCreditsModal
                open={!!insufficientCredits}
                needed={insufficientCredits?.needed}
                operation={insufficientCredits?.operation}
                message={insufficientCredits?.message}
                onClose={handleCloseInsufficient}
            />
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        error: state.error.error,
    };
}
const mapDispatchToProps = (dispatch) => ({
    errorDimiss: () => dispatch(DismissError()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Error);
